// İçerik performansı grafikleri (MOD-12): top içerik için gruplu Bar (view/click
// yan yana) + toplam dağılım için Pie. recharts inline (vendor-charts chunk).

import { useMemo } from 'react';
import { Card, Col, Empty, Row } from 'antd';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ContentPerformanceItem } from '../types';

const VIEW_COLOR = '#2563eb';
const CLICK_COLOR = '#16a34a';
const TOP_N = 10;

interface ChartLabels {
  topTitle: string;
  distributionTitle: string;
  views: string;
  clicks: string;
  empty: string;
}

interface ContentPerformanceChartsProps {
  items: readonly ContentPerformanceItem[];
  labels: ChartLabels;
  untitled: string;
}

function formatNumber(n: number): string {
  return Number.isFinite(n) ? n.toLocaleString('tr-TR') : '0';
}

export function ContentPerformanceCharts({
  items,
  labels,
  untitled,
}: ContentPerformanceChartsProps) {
  // En çok etkileşim alan (view+click) ilk N içerik — gruplu bar.
  const barData = useMemo(
    () =>
      [...items]
        .sort((a, b) => b.views + b.clicks - (a.views + a.clicks))
        .slice(0, TOP_N)
        .map((it) => ({
          name: (it.title || it.slug || untitled).slice(0, 40),
          views: it.views,
          clicks: it.clicks,
        })),
    [items, untitled],
  );

  const totals = useMemo(() => {
    const views = items.reduce((sum, it) => sum + it.views, 0);
    const clicks = items.reduce((sum, it) => sum + it.clicks, 0);
    return { views, clicks };
  }, [items]);

  const pieData = useMemo(
    () => [
      { key: 'views', name: labels.views, value: totals.views, color: VIEW_COLOR },
      { key: 'clicks', name: labels.clicks, value: totals.clicks, color: CLICK_COLOR },
    ],
    [labels.views, labels.clicks, totals.views, totals.clicks],
  );

  const hasData = barData.length > 0 && totals.views + totals.clicks > 0;

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
      <Col xs={24} lg={16}>
        <Card title={labels.topTitle} data-testid="content-perf-bar">
          {hasData ? (
            <ResponsiveContainer width="100%" height={Math.max(240, barData.length * 40)}>
              <BarChart
                data={barData}
                layout="vertical"
                margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--ds-border-subtle, #eef2f7)" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(value: number, key: string) => [
                    formatNumber(value),
                    key === 'views' ? labels.views : labels.clicks,
                  ]}
                />
                <Legend />
                <Bar dataKey="views" name={labels.views} fill={VIEW_COLOR} radius={[0, 4, 4, 0]} />
                <Bar dataKey="clicks" name={labels.clicks} fill={CLICK_COLOR} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Empty description={labels.empty} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Card>
      </Col>
      <Col xs={24} lg={8}>
        <Card title={labels.distributionTitle} data-testid="content-perf-pie">
          {hasData ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  isAnimationActive={false}
                  label={(entry: { value?: number }) => formatNumber(entry.value ?? 0)}
                >
                  {pieData.map((slice) => (
                    <Cell key={slice.key} fill={slice.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatNumber(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Empty description={labels.empty} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Card>
      </Col>
    </Row>
  );
}
